import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { FilterField } from "kontrolEdge/helpers/advancedFilters";
import { DurationBadge } from "kontrolEdge/helpers/DurationBadge";
import { formatDuration } from "kontrolEdge/helpers/formatDuration";
import { parseRecordDate } from "../OverviewComponents/actionCenter/overviewModel";
import {
  FailoverItem,
  FailoverTableRow,
  MaintenanceItem,
  MaintenanceTableRow,
} from "../OverviewComponents/Types";

/**
 * Both tabs read their rows in the shape the Overview opens them in, so a
 * record shows the same duration ("1h 5m") and the same details modal from
 * either screen.
 */
export function toOutageRow(record: FailoverItem): FailoverTableRow {
  return {
    date: record.date,
    description: record.description,
    customer: record.customer?.name ?? "–",
    duration: formatDuration(record.duration),
    site: record.site ?? "–",
    summary: record.summary ?? "",
    detail: record.detail ?? "",
    incidentId: record.incidentId ?? "",
  };
}

export function toMaintenanceRow(record: MaintenanceItem): MaintenanceTableRow {
  return {
    date: record.date,
    description: record.description,
    customer: record.customer?.name ?? "–",
    duration: formatDuration(record.duration),
  };
}

/** Records carry a date-only string ("9/24/2026"): sort on the day, not the text. */
const byDay = (row: { date: string }) => parseRecordDate(row.date);

/** Absent values read as "—" in every cell, the list convention elsewhere in Kontrol Edge. */
const plain = (value: string | undefined) => (value && value !== "–" ? value : "—");

function DetailsLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="font-medium text-semantic-text-brand underline hover:text-semantic-text-brand focus:underline"
    >
      Details
    </button>
  );
}

const customerColumn = {
  header: "Customer",
  accessor: "customer",
  width: "160px",
  wrapMode: "truncate",
  renderCell: (row: { customer?: string }) => plain(row.customer),
} as const;

/**
 * Two lines and the rest in Details. The clamp sits on an inner block: the
 * table's own `clamp-2` lands on the `<td>`, where `line-clamp` has no effect.
 */
const descriptionColumn = {
  header: "Description",
  accessor: "description",
  wrapMode: "wrap",
  renderCell: (row: { description: string }) => (
    <span className="line-clamp-2" title={row.description}>
      {row.description}
    </span>
  ),
} as const;

export function outageColumns(
  isViewingAllCustomers: boolean,
  onOpen: (row: FailoverTableRow) => void
): GenericTableColumn<FailoverTableRow>[] {
  return [
    {
      header: "ID",
      accessor: "incidentId",
      width: "110px",
      wrapMode: "nowrap",
      renderCell: (row) => plain(row.incidentId),
    },
    { header: "Date", accessor: "date", width: "110px", wrapMode: "nowrap", sortAccessor: byDay },
    ...(isViewingAllCustomers ? [customerColumn] : []),
    {
      header: "Site",
      accessor: "site",
      width: "150px",
      wrapMode: "nowrap",
      renderCell: (row) =>
        row.site && row.site !== "–" ? (
          <span className="inline-flex items-center rounded bg-semantic-bg-secondary px-2 py-0.5 text-xs font-medium text-semantic-text-primary">
            {row.site}
          </span>
        ) : (
          "—"
        ),
    },
    descriptionColumn,
    {
      header: "Duration",
      accessor: "duration",
      width: "110px",
      wrapMode: "nowrap",
      renderCell: (row) => <DurationBadge value={row.duration} />,
    },
    {
      id: "details",
      header: "",
      width: "80px",
      sortable: false,
      renderCell: (row) => <DetailsLink onClick={() => onOpen(row)} />,
    },
  ];
}

export function maintenanceColumns(
  isViewingAllCustomers: boolean,
  onOpen: (row: MaintenanceTableRow) => void
): GenericTableColumn<MaintenanceTableRow>[] {
  return [
    { header: "Date", accessor: "date", width: "110px", wrapMode: "nowrap", sortAccessor: byDay },
    ...(isViewingAllCustomers ? [customerColumn] : []),
    descriptionColumn,
    { header: "Duration", accessor: "duration", width: "110px", wrapMode: "nowrap" },
    {
      id: "details",
      header: "",
      width: "80px",
      sortable: false,
      renderCell: (row) => <DetailsLink onClick={() => onOpen(row)} />,
    },
  ];
}

const textField = <T,>(label: string, accessor: (row: T) => string | undefined): FilterField<T> => ({
  label,
  accessor: (row) => accessor(row) ?? "",
  type: "string",
  inFreeText: true,
});

export function makeOutageFields(isViewingAllCustomers: boolean): FilterField<FailoverTableRow>[] {
  return [
    textField<FailoverTableRow>("ID", (row) => row.incidentId),
    textField<FailoverTableRow>("Date", (row) => row.date),
    ...(isViewingAllCustomers ? [textField<FailoverTableRow>("Customer", (row) => row.customer)] : []),
    textField<FailoverTableRow>("Site", (row) => row.site),
    textField<FailoverTableRow>("Description", (row) => row.description),
  ];
}

export function makeMaintenanceFields(isViewingAllCustomers: boolean): FilterField<MaintenanceTableRow>[] {
  return [
    textField<MaintenanceTableRow>("Date", (row) => row.date),
    ...(isViewingAllCustomers ? [textField<MaintenanceTableRow>("Customer", (row) => row.customer)] : []),
    textField<MaintenanceTableRow>("Description", (row) => row.description),
  ];
}
